import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import {
  Carte,
  Chargement,
  DateHeure,
  Erreur,
  Etiquette,
  Montant,
  Nombre,
  Vide,
} from '../../composants/communs.tsx';
import type { LigneStock, Mouvement } from './types.ts';

/** Libellés des types de mouvement, dans la langue du comptoir. */
const LIBELLE: Record<string, string> = {
  RECEPTION: 'Réception',
  VENTE: 'Vente',
  TRANSFERT_SORTIE: 'Transfert — départ',
  TRANSFERT_ENTREE: 'Transfert — arrivée',
  AJUSTEMENT: 'Ajustement d’inventaire',
};

/**
 * Journal des mouvements d'un produit dans une boutique — RG-040.
 *
 * Le stock affiché ailleurs est un cache reconstructible ; ceci en est la
 * source. C'est l'écran qu'on ouvre quand un chiffre surprend, et la seule
 * réponse possible à « d'où sortent ces douze sacs ? ».
 *
 * Un mouvement ne se corrige jamais : on écrit l'inverse, et les deux
 * restent. L'historique dit donc l'histoire entière, ratés compris.
 */
export function MouvementsProduit({
  ligne,
  onFerme,
}: {
  ligne: LigneStock;
  onFerme: () => void;
}): ReactNode {
  const { data, isPending, error } = useQuery({
    queryKey: ['mouvements', ligne.produit_id, ligne.boutique_id],
    queryFn: () =>
      appeler<{ donnees: Mouvement[] }>(
        `/mouvements?produit_id=${ligne.produit_id}&boutique_id=${ligne.boutique_id}`,
      ),
    select: (reponse) => reponse.donnees,
  });
  const avecCouts = (data ?? []).some((mouvement) => mouvement.cout_unitaire !== undefined);

  return (
    <Carte
      titre={`${ligne.designation} — ${ligne.boutique}`}
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Fermer
        </button>
      }
    >
      <Erreur erreur={error} />
      {isPending ? (
        <Chargement />
      ) : data === undefined || data.length === 0 ? (
        <Vide texte="Aucun mouvement enregistré pour ce produit." />
      ) : (
        <>
          <p>
            Stock actuel : <Nombre valeur={ligne.quantite} />. Le stock est un cache ;
            ces mouvements en sont la source.
          </p>
          <div className="tableau-defilant">
            <table>
              <thead>
                <tr>
                  <th>Quand</th>
                  <th>Nature</th>
                  <th>Quantité</th>
                  <th>Document</th>
                  <th>Par</th>
                  {avecCouts && <th>Coût unitaire</th>}
                </tr>
              </thead>
              <tbody>
                {data.map((mouvement) => (
                  <tr key={mouvement.id}>
                    <td>
                      <DateHeure valeur={mouvement.horodatage_serveur} />
                    </td>
                    <td>{LIBELLE[mouvement.type] ?? mouvement.type}</td>
                    <td>
                      {/* Le signe porte l'information : une sortie est un
                          retrait, et l'afficher comme une entrée serait un
                          contresens. */}
                      <Etiquette
                        ton={mouvement.quantite < 0 ? 'rouge' : 'vert'}
                        texte={
                          mouvement.quantite > 0
                            ? `+${String(mouvement.quantite)}`
                            : String(mouvement.quantite)
                        }
                      />
                    </td>
                    <td className="mono">{mouvement.document_numero ?? mouvement.document_type}</td>
                    <td>{mouvement.utilisateur}</td>
                    {avecCouts && (
                      <td>
                        <Montant valeur={mouvement.cout_unitaire} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Carte>
  );
}
